/**
 * Parsing State Store
 *
 * Manages active parsing tasks state with persistence
 */

import { create } from 'zustand'
import { persist, createJSONStorage, type StateStorage } from 'zustand/middleware'
import { notification } from 'antd'

// * Module-level флаг: показываем quota-warning только один раз за сессию,
// чтобы не спамить юзера notification'ами при каждом setItem.
let quotaWarned = false

const notifyQuotaExceededOnce = () => {
  if (quotaWarned) return
  quotaWarned = true
  notification.warning({
    message: 'Не хватает места в localStorage',
    description: 'Старые данные удаляются автоматически. Закрой и переоткрой модалку для синхронизации с сервером.',
    duration: 10,
  })
}

// * Custom storage adapter with quota error handling
const safeLocalStorage: StateStorage = {
  getItem: (name) => {
    try {
      return localStorage.getItem(name)
    } catch (error) {
      console.warn('localStorage.getItem failed:', error)
      return null
    }
  },
  setItem: (name, value) => {
    try {
      localStorage.setItem(name, value)
    } catch (error) {
      // QuotaExceededError - localStorage is full
      console.warn('localStorage.setItem failed (quota exceeded?):', error)
      notifyQuotaExceededOnce()
      // Try to clear old data and retry
      try {
        localStorage.removeItem(name)
        localStorage.setItem(name, value)
      } catch {
        // Still failing, just log and continue without persistence
        console.error('Unable to persist state to localStorage')
      }
    }
  },
  removeItem: (name) => {
    try {
      localStorage.removeItem(name)
    } catch (error) {
      console.warn('localStorage.removeItem failed:', error)
    }
  },
}

// Re-export ManualWorkflowState type for use in hooks
export type { ManualWorkflowState }

interface ActiveParsingTask {
  task_id: string
  project_id: number
  project_name: string
  status: 'pending' | 'running' | 'completed' | 'failed' | 'uploaded'
  progress: number
  current_step: string | null
  started_at: string
  completed_at?: string
  error_message?: string
}

/**
 * Active AI analysis batch state
 */
interface ActiveAnalysisBatch {
  batch_id: number
  project_id: number
  status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled'
  total: number
  processed: number
  started_at: string
}

/**
 * Manual workflow state - saved when user is in manual input flow
 * Allows restoration after page refresh
 *
 * NOTE: We intentionally DO NOT save large arrays (domains, parsing_result)
 * to localStorage to avoid quota exceeded errors. These are restored from
 * the server when resuming a workflow.
 */
interface ManualWorkflowState {
  project_id: number
  project_name: string
  input_method: 'manual' | 'yandex'
  current_step: number
  task_id: string
  // domains and parsing_result are NOT stored - restored from server
  ai_config?: any
  ai_filter: 'all' | 'approved' | 'rejected' | 'uncertain'
  ai_search_text: string
  selected_phones: string[]
  saved_at: string
}

interface ParsingState {
  // * Map of projectId -> active task
  activeTasksByProject: Record<number, ActiveParsingTask>

  // * Map of projectId -> manual workflow state (for page refresh recovery)
  manualWorkflowByProject: Record<number, ManualWorkflowState>

  // * Map of projectId -> active AI analysis batch
  activeAnalysisByProject: Record<number, ActiveAnalysisBatch>

  // * Actions
  setActiveTask: (projectId: number, task: ActiveParsingTask) => void
  updateTaskProgress: (projectId: number, progress: number, currentStep?: string) => void
  updateTaskStatus: (projectId: number, status: ActiveParsingTask['status'], errorMessage?: string) => void
  completeTask: (projectId: number) => void
  clearTask: (projectId: number) => void
  getActiveTask: (projectId: number) => ActiveParsingTask | null

  // * Manual workflow actions
  saveManualWorkflow: (projectId: number, state: Omit<ManualWorkflowState, 'saved_at'>) => void
  getManualWorkflow: (projectId: number) => ManualWorkflowState | null
  clearManualWorkflow: (projectId: number) => void

  // * AI Analysis batch actions
  setActiveAnalysis: (projectId: number, batch: ActiveAnalysisBatch) => void
  updateAnalysisProgress: (projectId: number, processed: number, status?: ActiveAnalysisBatch['status']) => void
  clearActiveAnalysis: (projectId: number) => void
  getActiveAnalysis: (projectId: number) => ActiveAnalysisBatch | null
}

export const useParsingStore = create<ParsingState>()(
  persist(
    (set, get) => ({
      activeTasksByProject: {},
      manualWorkflowByProject: {},
      activeAnalysisByProject: {},

      setActiveTask: (projectId, task) => {
        set((state) => ({
          activeTasksByProject: {
            ...state.activeTasksByProject,
            [projectId]: task,
          },
        }))
      },
      
      updateTaskProgress: (projectId, progress, currentStep) => {
        set((state) => {
          const existingTask = state.activeTasksByProject[projectId]
          if (!existingTask) return state
          
          return {
            activeTasksByProject: {
              ...state.activeTasksByProject,
              [projectId]: {
                ...existingTask,
                progress,
                current_step: currentStep || existingTask.current_step,
              },
            },
          }
        })
      },
      
      updateTaskStatus: (projectId, status, errorMessage) => {
        set((state) => {
          const existingTask = state.activeTasksByProject[projectId]
          if (!existingTask) return state
          
          return {
            activeTasksByProject: {
              ...state.activeTasksByProject,
              [projectId]: {
                ...existingTask,
                status,
                error_message: errorMessage,
                ...(status === 'completed' && { completed_at: new Date().toISOString() }),
              },
            },
          }
        })
      },
      
      completeTask: (projectId) => {
        set((state) => {
          const existingTask = state.activeTasksByProject[projectId]
          if (!existingTask) return state
          
          return {
            activeTasksByProject: {
              ...state.activeTasksByProject,
              [projectId]: {
                ...existingTask,
                status: 'completed',
                progress: 100,
                completed_at: new Date().toISOString(),
              },
            },
          }
        })
      },
      
      clearTask: (projectId) => {
        set((state) => {
          const newTasks = { ...state.activeTasksByProject }
          delete newTasks[projectId]
          return { activeTasksByProject: newTasks }
        })
      },
      
      getActiveTask: (projectId) => {
        return get().activeTasksByProject[projectId] || null
      },

      // * Manual workflow actions
      saveManualWorkflow: (projectId, state) => {
        set((s) => ({
          manualWorkflowByProject: {
            ...s.manualWorkflowByProject,
            [projectId]: {
              ...state,
              saved_at: new Date().toISOString(),
            },
          },
        }))
      },

      getManualWorkflow: (projectId) => {
        return get().manualWorkflowByProject[projectId] || null
      },

      clearManualWorkflow: (projectId) => {
        set((s) => {
          const newWorkflows = { ...s.manualWorkflowByProject }
          delete newWorkflows[projectId]
          return { manualWorkflowByProject: newWorkflows }
        })
      },

      // * AI Analysis batch actions
      setActiveAnalysis: (projectId, batch) => {
        set((state) => ({
          activeAnalysisByProject: {
            ...state.activeAnalysisByProject,
            [projectId]: batch,
          },
        }))
      },

      updateAnalysisProgress: (projectId, processed, status) => {
        set((state) => {
          const existing = state.activeAnalysisByProject[projectId]
          if (!existing) return state

          return {
            activeAnalysisByProject: {
              ...state.activeAnalysisByProject,
              [projectId]: {
                ...existing,
                processed,
                status: status || existing.status,
              },
            },
          }
        })
      },

      clearActiveAnalysis: (projectId) => {
        set((state) => {
          const newAnalysis = { ...state.activeAnalysisByProject }
          delete newAnalysis[projectId]
          return { activeAnalysisByProject: newAnalysis }
        })
      },

      getActiveAnalysis: (projectId) => {
        return get().activeAnalysisByProject[projectId] || null
      },
    }),
    {
      name: 'parsing-state-storage',
      storage: createJSONStorage(() => safeLocalStorage),
      // * Only persist tasks and workflows for a limited time
      partialize: (state) => {
        const filteredTasks: Record<number, ActiveParsingTask> = {}
        const filteredWorkflows: Record<number, ManualWorkflowState> = {}
        const filteredAnalysis: Record<number, ActiveAnalysisBatch> = {}
        const oneHourAgo = new Date(Date.now() - 60 * 60 * 1000)

        Object.entries(state.activeTasksByProject).forEach(([projectId, task]) => {
          // * Keep running/pending tasks
          if (task.status === 'running' || task.status === 'pending') {
            filteredTasks[Number(projectId)] = task
          }
          // * Keep completed tasks for 1 hour
          else if (task.status === 'completed' && task.completed_at) {
            const completedAt = new Date(task.completed_at)
            if (completedAt > oneHourAgo) {
              filteredTasks[Number(projectId)] = task
            }
          }
        })

        // * Keep manual workflows for 1 hour
        Object.entries(state.manualWorkflowByProject).forEach(([projectId, workflow]) => {
          if (workflow.saved_at) {
            const savedAt = new Date(workflow.saved_at)
            if (savedAt > oneHourAgo) {
              filteredWorkflows[Number(projectId)] = workflow
            }
          }
        })

        // * Keep running AI analysis batches (max 2 hours to prevent stale entries)
        const twoHoursAgo = new Date(Date.now() - 2 * 60 * 60 * 1000)
        Object.entries(state.activeAnalysisByProject).forEach(([projectId, batch]) => {
          if (batch.status === 'running' || batch.status === 'pending') {
            // Drop stale batches that have been "running" for over 2 hours
            if (batch.started_at) {
              const startedAt = new Date(batch.started_at)
              if (startedAt < twoHoursAgo) {
                console.log(`🧹 Dropping stale ${batch.status} batch #${batch.batch_id} from localStorage (started ${batch.started_at})`)
                return // skip - don't persist
              }
            }
            filteredAnalysis[Number(projectId)] = batch
          }
        })

        return {
          activeTasksByProject: filteredTasks,
          manualWorkflowByProject: filteredWorkflows,
          activeAnalysisByProject: filteredAnalysis,
        }
      },
      // * Handle deserialization errors and clean up stale data on load
      onRehydrateStorage: () => (state, error) => {
        if (error) {
          console.error('Failed to rehydrate parsing store:', error)
          // ! Clear corrupted storage
          localStorage.removeItem('parsing-state-storage')
          return
        }

        // * Clean up stale running/pending batches on page load
        if (state) {
          const twoHoursAgo = new Date(Date.now() - 2 * 60 * 60 * 1000)
          const staleProjects: number[] = []

          Object.entries(state.activeAnalysisByProject).forEach(([projectId, batch]) => {
            if ((batch.status === 'running' || batch.status === 'pending') && batch.started_at) {
              const startedAt = new Date(batch.started_at)
              if (startedAt < twoHoursAgo) {
                staleProjects.push(Number(projectId))
              }
            }
          })

          // Clear stale entries
          staleProjects.forEach((projectId) => {
            console.log(`🧹 Clearing stale analysis for project ${projectId} on page load`)
            state.clearActiveAnalysis(projectId)
          })
        }
      },
    }
  )
)

